import type { CoralState, GameEvent, HeatStressState, Sanctuary, World } from '../types';
import { DEPTH_BINS, binHeatFallC, type DepthBin } from '../reef';
import type { SstRecord } from '../sstRecord';
import { recordedC } from '../sstRecord';
import { CORAL_RATES, episodeKilled, nextCoral, reefBleached, reefCover, spawningOn, type CoralRates, type Spawning } from '../coral';
import { CORAL_KIND_SHARES } from '../data/reef/kinds';
import { NO_HEAT_STRESS, nextHeatStress } from '../heatStress';
import { spawningOf } from '../data/sites';
import { MAUI_NUI_REEF } from '../data/reef/mauiNui';
import type { Reef } from '../reef';
import { epochDayOf } from '../calendar';
import { held } from '../held';
import { hotSpotC } from './heatStress';
import { sanctuaryRecord } from './climate';
import { bottomAround, herbivoreRatio } from '../grazing';
import { FISH_ISLANDS, ISLAND_HARD_BOTTOM_M2, classWeightsKg, monthStep } from '../fish';
import { PROTECTED_HERBIVORES } from '../management';
import { REEF_SPECIES } from './fish';
import type { ReefFish } from '../types';

/**
 * Whether a reef is the one its bottom (`REEF_BOTTOM`), its kinds (`CORAL_KIND_SHARES`) and its herbivores'
 * (`FISH_ISLANDS`, `ISLAND_HARD_BOTTOM_M2`) tables were built for: Maui Nui's, by its own record (review 26: they were
 * taken by any reef of its strata's count, a second reef site opening on Maui Nui's bottom, kinds and grazing silently).
 */
export function hasMauiNuiTables(reef: Reef): boolean {
  return reef === MAUI_NUI_REEF;
}

/**
 * Each island's herbivores against their tables' year (M167, `herbivoreRatio`): the protected herbivores
 * (`PROTECTED_HERBIVORES`: the bullethead, the palenose, the redlip, the kala, since M178 the nenue and since M220 the manini), kg a km² of the
 * island's hard bottom today over the year's; none where the sanctuary has no fish.
 */
export function herbivoreRatios(fish: ReefFish | null, epochDay: number): number[] | undefined {
  return herbivoreKgKm2(fish, epochDay)?.map((x, i) => herbivoreRatio(i, x));
}

/** Each island's protected herbivores today, kg a km² of its hard bottom (M274: what a fish census of an island counts); none where the sanctuary has no fish. */
export function herbivoreKgKm2(fish: ReefFish | null, epochDay: number): number[] | undefined {
  if (!fish) return undefined;
  const step = monthStep(epochDay);
  const kg = FISH_ISLANDS.map(() => 0);
  for (const sp of REEF_SPECIES) {
    if (!PROTECTED_HERBIVORES.includes(sp.life.id)) continue;
    const weights = classWeightsKg(sp.life)[step] ?? [];
    const islands = fish[sp.life.id].islands;
    for (let i = 0; i < islands.length; i++) {
      const counts = islands[i] as readonly number[];
      let island = 0;
      for (let c = 0; c < counts.length; c++) island += (counts[c] as number) * (weights[c] ?? 0);
      kg[i] = (kg[i] as number) + island;
    }
  }
  return kg.map((x, i) => (x * 1e6) / (ISLAND_HARD_BOTTOM_M2[i] as number));
}

/**
 * Step 2 of the tick, production (M47): each sanctuary's coral grows,
 * bleaches and dies under the heat stress step 1 left it, and on its spawning
 * nights takes the recruits its larvae bring (M68, `coral.ts`). Nothing here
 * draws.
 */

/** The bleaching the log names when it first passes, percent of the live coral (SET BY HAND: a fifth, the share `coral.ts` bleaches at the program's first alert level). */
const LOGGED_BLEACHED_PERCENT = 20;
/** The smallest loss an episode's end is logged for, percent of the live coral (SET BY HAND: a choice). */
const LOGGED_LOSS_PERCENT = 1;

/** Each depth bin as the log names it (review M50: by its name, so a bin the log does not know cannot be written as "undefined"). */
const BIN_WORDS: Readonly<Record<DepthBin, string>> = { shallow: 'in the shallows', mid: 'at mid-depth', deep: 'on the deep reef' };

/** Each sanctuary's coral, a day on, and the log's word when bleaching passes a fifth and when an episode that killed coral ends. */
export function stepCoral(world: World, events: GameEvent[]): void {
  for (const s of world.sanctuaries) {
    const spawning = spawningOf(s);
    if (!s.coral || !spawning) continue;
    const { reef } = spawning;
    const before = s.coral;
    s.coral = nextCoral(before, s.heatStress, binHeatFallC(reef), spawningOn(epochDayOf(world), spawning), CORAL_RATES, hasMauiNuiTables(reef) ? herbivoreRatios(s.fish, epochDayOf(world)) : undefined);
    const was = reefBleached(reef, before);
    const now = reefBleached(reef, s.coral);
    if (was < LOGGED_BLEACHED_PERCENT && now >= LOGGED_BLEACHED_PERCENT) {
      events.push({
        day: world.day,
        severity: 'warning',
        title: `The corals around ${s.site.placeName} are bleaching`,
        detail: `${Math.round(now)}% of the live coral has lost its color in the heat. Bleached coral is alive, and recovers if the heat eases in time.`,
      });
    }
    const ended = before.answeredDhw.some((d) => d > 0) && s.coral.answeredDhw.every((d) => d === 0);
    const killed = ended ? episodeKilled(reef, before) : null;
    if (killed && killed.all >= LOGGED_LOSS_PERCENT) {
      const series = (xs: string[]): string => `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
      const heat = series(before.answeredDhw.map((d, k) => `${d.toFixed(1)}${k === 0 ? ' degree heating weeks' : ''} ${BIN_WORDS[(DEPTH_BINS[k] as (typeof DEPTH_BINS)[number]).bin]}`));
      const deaths = series(killed.byBin.map((x) => `${Math.round(x)}%`));
      events.push({
        day: world.day,
        severity: 'critical',
        title: `The heat killed ${Math.round(killed.all)}% of the live coral around ${s.site.placeName}`,
        detail: `The episode reached ${heat}, killing ${deaths} of the live coral there. Live coral now covers ${reefCover(reef, s.coral).toFixed(1)}% of the reef's floor.`,
      });
    }
  }
}

/** The year before a survey its heat is followed over, so an episode under way at the survey is known (SET BY HAND, as a world's opening follows it, `heatStress.ts`: longer than the record's longest episode, MEASURED 189 days). */
const DAYS_BEFORE_SURVEY = 365;

/**
 * The coral as a survey found it, carried to a day through the record: each
 * stratum's cover the survey's (a stratum it did not survey takes the
 * nearest survey's in time that did), then the record's own heat stress,
 * followed from a year before the survey, and the coral's step each day after
 * the survey's middle day, its larvae settling on the spawning nights (M68).
 * Up to that day the heat only sets the bleaching and what each depth bin has
 * answered, which the survey's cover already answers. Only through the days
 * the record has; past its end the coral is held.
 */
export function carryCoral(spawning: Spawning, record: SstRecord, survey: number, toEpochDay: number, rates: CoralRates = CORAL_RATES): CoralState {
  const { reef } = spawning;
  const s = reef.surveys[survey];
  if (!s) throw new Error(`no survey ${survey}`);
  const coverPercent = s.strata.map((x, i) => {
    if (x.coverPercent !== null) return x.coverPercent;
    const others = reef.surveys.map((o, k) => ({ k, v: o.strata[i]?.coverPercent ?? null })).filter((o) => o.v !== null);
    const nearest = others.sort((a, b) => Math.abs(a.k - survey) - Math.abs(b.k - survey) || a.k - b.k)[0];
    if (!nearest) throw new Error(`no survey has stratum ${i}`);
    return nearest.v as number;
  });
  const none = (): number[] => DEPTH_BINS.map(() => 0);
  // Its bottom (M167): the survey's turf and macroalgae around its coral, where the reef has its surveys' bottom.
  const tables = hasMauiNuiTables(reef);
  const around = tables ? coverPercent.map((x, i) => bottomAround(i, x)) : null;
  const bottom = around ? { turfPercent: around.map((b) => b.turfPercent), macroalgaePercent: around.map((b) => b.macroalgaePercent) } : undefined;
  // Its kinds (M176): each stratum's cover shared as NOAA's colonies share it, where the reef has its surveys' kinds.
  const kindPercent = tables ? splitByKind(coverPercent) : undefined;
  let c: CoralState = { coverPercent, ...(kindPercent ? { kindPercent } : {}), bleachedPercent: none(), answeredDhw: none(), ...(bottom ? { bottom } : {}) };
  if (record.kind !== 'daily') return c;
  let h: HeatStressState = { ...NO_HEAT_STRESS, countedC: [...NO_HEAT_STRESS.countedC] };
  const falls = binHeatFallC(reef);
  const end = Math.min(toEpochDay, record.lastEpochDay);
  for (let d = s.medianEpochDay - DAYS_BEFORE_SURVEY + 1; d <= end; d++) {
    const departure = recordedC(record, d);
    if (departure === null) continue;
    h = nextHeatStress(h, hotSpotC(record, departure, d) as number);
    const next = nextCoral(c, h, falls, spawningOn(d, spawning), rates);
    c = d <= s.medianEpochDay ? { ...next, coverPercent: c.coverPercent, ...(c.kindPercent ? { kindPercent: c.kindPercent } : {}), ...(c.bottom ? { bottom: c.bottom } : {}) } : next;
  }
  return c;
}

/**
 * Each stratum's cover by kind (M176): its cover shared as NOAA's colonies share it there (`CORAL_KIND_SHARES`), each
 * held to six places, the last kind what the others leave, so their sum is the cover to the rounding.
 */
function splitByKind(coverPercent: readonly number[]): number[][] {
  return coverPercent.map((x, i) => {
    const shares = CORAL_KIND_SHARES[i] as readonly number[];
    const first = shares.slice(0, -1).map((w) => held(x * w));
    return [...first, held(Math.max(0, x - first.reduce((a, b) => a + b, 0)))];
  });
}

/**
 * A sanctuary's coral on the day a world opens: the latest survey whose
 * middle day is on or before it, carried to it (`carryCoral`); before the
 * first survey, the first survey as it found the reef (open, M47).
 */
export function openingCoral(spawning: Spawning, record: SstRecord, startEpochDay: number): CoralState {
  let survey = 0;
  spawning.reef.surveys.forEach((s, k) => { if (s.medianEpochDay <= startEpochDay) survey = k; });
  return carryCoral(spawning, record, survey, startEpochDay);
}

/** A new sanctuary's coral at its site, or null where its reef has not been built. */
export function sanctuaryOpeningCoral(s: Pick<Sanctuary, 'siteId' | 'regionId'>, startEpochDay: number): CoralState | null {
  const spawning = spawningOf(s);
  return spawning ? openingCoral(spawning, sanctuaryRecord(s), startEpochDay) : null;
}
